import { useState, useEffect, useCallback, useRef } from "react";

interface UseInfiniteScrollOptions {
  initialLimit?: number;
  threshold?: number;
  // Set to false when the caller owns fetching page 1 itself (e.g. to catch
  // and surface errors) and only wants this hook for scroll-driven paging.
  // Defaults to true so existing callers that rely on the hook's own mount
  // fetch are unaffected.
  autoLoad?: boolean;
}

interface UseInfiniteScrollResult<T> {
  items: T[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: (items: T[]) => void;
  removeItem: (id: string) => void;
  _requestData?: () => void;
}

export const useInfiniteScroll = <T extends { _id?: string }>(
  fetchData: (param: any) => Promise<T[]>,
  params: any,
  options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollResult<T> => {
  const { initialLimit = 10, threshold = 200, autoLoad = true } = options;

  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);

  // Always holds the latest params without forcing loadMore to be
  // re-memoized on every filter change (params is typically a fresh object
  // each render). Without this, a scroll-triggered page fetch could fire
  // with whatever filters were current the last time loadMore's own deps
  // (page/loading/hasMore/initialLimit/fetchData) happened to change,
  // rather than the filters visible on screen right now.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // Function to fetch more data
  const loadMore = useCallback(async () => {
    if (loading || loadingRef.current || !hasMore) return;
    loadingRef.current = true;

    setLoading(true);
    try {
      const res = await fetchData({ page, limit: initialLimit, ...paramsRef.current });
      const data = res.data;

      if (Array.isArray(data)) {
        if (data.length < initialLimit) {
          setHasMore(false);
        }

        setItems((prev) => {
          const newItems = data.filter(
            (newItem) => !prev.some((prevItem) => prevItem._id === newItem._id)
          );
          return [...prev, ...newItems];
        });

        setPage((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error loading more items:", error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [page, loading, hasMore, initialLimit, fetchData]);

  const _requestData = useCallback(() => {
    loadingRef.current = true;
    setLoading(true);
    try {
      fetchData({ page: "1", limit: initialLimit, ...paramsRef.current }).then(
        (res: any) => {
          console.log("res.list..", res);
          const data = res.data;
          if (Array.isArray(data)) {
            if (data.length < initialLimit) {
              setHasMore(false);
            }
            setItems(data);
            setPage(2);
          }
        }
      );
    } catch (error) {
      console.error("Error loading more items:", error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Handle scroll event
  useEffect(() => {
    const handleScroll = debounce(() => {
      if (
        window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - threshold &&
        page > 1
      ) {
        loadMore();
      }
    }, 300); // 100ms debounce

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loadMore, threshold]);

  // Initial load
  useEffect(() => {
    if (autoLoad && items.length === 0) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset function — used both on mount-equivalent refresh and whenever a
  // filter change requires discarding the current page and starting over.
  // hasMore is re-armed on a full page so scrolling works again after a
  // previous filter had exhausted the list.
  const reset = (newItems: T[]) => {
    setItems(newItems);
    setPage(2);
    setHasMore(newItems.length >= initialLimit);
    setLoading(false);
  };

  // Drops a single item from the current page without a refetch — used
  // after a successful delete.
  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item._id !== id));
  };

  return {
    items,
    loading,
    hasMore,
    loadMore,
    reset,
    removeItem,
    _requestData,
  };
};
